import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useLocation, matchPath, Link } from 'react-router-dom';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Grow from '@mui/material/Grow';
import Divider from '@mui/material/Divider';
import { useGetMenuMaster } from './../../../../api/menu';
import useActivePath from '../../../../hooks/useActivePath';
import { useNavKeyboard } from './NavKeyboardContext';

export default function NavCollapse({ item, level = 1 }) {
  const { pathname } = useLocation();
  const activePath = useActivePath();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const { kbFocusedId, kbOpenIds } = useNavKeyboard();
  const isKbFocused = item.id === kbFocusedId;
  const isKbOpen = kbOpenIds.has(item.id);

  const isChildActive = !!item.children?.some((child) => {
    if (!child?.url) return false;
    try {
      return !!matchPath({ path: child.url, end: false }, activePath);
    } catch {
      return activePath === child.url || activePath.startsWith(child.url);
    }
  });

  const shouldAutoOpen = Boolean(item.autoOpen);
  const [open, setOpen] = useState(isChildActive || shouldAutoOpen);
  const [popperOpen, setPopperOpen] = useState(false);
  const anchorRef = useRef(null);

  const childItemRefs = useRef({});

  useEffect(() => {
    if (isKbFocused && anchorRef.current) {
      anchorRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isKbFocused]);

  useEffect(() => {
    const el = childItemRefs.current[kbFocusedId];
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [kbFocusedId]);

  useEffect(() => {
    if (item.autoOpen || isKbOpen) {
      setOpen(true);
    } else {
      setOpen(isChildActive);
    }
    if (popperOpen) setPopperOpen(false);
  }, [item.autoOpen, isChildActive, pathname, isKbOpen]);

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon fontSize="small" /> : null;

  const isChildSelected = (child) => {
    if (!child?.url) return false;
    try {
      return !!matchPath({ path: child.url, end: false }, activePath);
    } catch {
      return activePath === child.url || activePath.startsWith(child.url);
    }
  };

  const handleToggle = () => {
    if (!drawerOpen) {
      setPopperOpen((prev) => !prev);
      return;
    }
    setOpen((prev) => !prev);
  };

  const handleClickAway = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setPopperOpen(false);
  };

  const handleKeyDownAnchor = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(e);
    } else if (e.key === 'Escape') {
      setPopperOpen(false);
    }
  };

  return (
    <>
      <ListItemButton
        ref={anchorRef}
        onClick={handleToggle}
        onKeyDown={handleKeyDownAnchor}
        aria-haspopup={!drawerOpen}
        aria-expanded={drawerOpen ? open : popperOpen}
        sx={{
          pl: drawerOpen ? `${level * 8}px` : `${level * 8}px`,
          py: 0.85,
          borderRadius: '10px',
          mx: drawerOpen ? 1 : 0.5,
          mb: 0.25,
          transition: 'background-color 0.12s ease',
          '&:hover': { bgcolor: 'var(--appMain50)' },
          ...(isChildActive && drawerOpen && { bgcolor: 'var(--appMain50)' }),
          ...(isChildActive && !drawerOpen && { bgcolor: 'var(--appMain100)' }),
          ...(isKbFocused && drawerOpen && { boxShadow: 'inset 0 0 0 1.5px var(--appMain300)' })
        }}
      >
        {isChildActive && drawerOpen && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3,
              height: '55%',
              borderRadius: '0 3px 3px 0',
              bgcolor: 'var(--appMainMain)'
            }}
          />
        )}

        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: 0,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isChildActive ? 'var(--appMainMain)' : 'var(--appMain500)',
              transition: 'color 0.12s, background-color 0.12s'
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(drawerOpen || level !== 1) && (
          <ListItemText
            primary={
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: isChildActive ? 600 : 500,
                  color: isChildActive ? 'var(--appMain900)' : 'var(--appMain700)',
                  lineHeight: 1.2,
                  transition: 'color 0.12s'
                }}
              >
                {item.title}
              </Typography>
            }
          />
        )}
        {drawerOpen && (
          <Box
            sx={{
              color: open ? 'var(--appMain500)' : 'var(--appMain300)',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 0.15s'
            }}
          >
            {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
          </Box>
        )}
      </ListItemButton>

      {drawerOpen ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ position: 'relative', ml: `${level * 8}px`, pl: '18px', mr: 1 }}>
            {item.children?.map((child) =>
              child.type === 'item' ? (
                <ListItemButton
                  ref={(el) => {
                    if (el) childItemRefs.current[child.id] = el;
                    else delete childItemRefs.current[child.id];
                  }}
                  component={child.url ? Link : 'div'}
                  to={child.url || undefined}
                  key={child.id}
                  selected={isChildSelected(child)}
                  sx={{
                    pl: '12px',
                    py: 0.7,
                    borderRadius: '8px',
                    mb: 0.25,
                    transition: 'background-color 0.12s',
                    '&:hover': { bgcolor: 'var(--appMain50)' },
                    '&.Mui-selected': {
                      bgcolor: '#dce7f4',
                      '&:hover': { bgcolor: '#dce7f4' }
                    },
                    ...(child.id === kbFocusedId && {
                      boxShadow: 'inset 0 0 0 1.5px var(--appMain300)'
                    })
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 20 }}>
                    <FiberManualRecordIcon
                      sx={{
                        fontSize: isChildSelected(child) ? 7 : 5.5,
                        color: isChildSelected(child) ? 'var(--appMainMain)' : 'var(--appMain300)',
                        transition: 'font-size 0.12s, color 0.12s'
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: isChildSelected(child) ? 600 : 400,
                          color: isChildSelected(child) ? 'var(--appMain900)' : 'var(--appMain600)',
                          transition: 'color 0.12s',
                          lineHeight: 1.2
                        }}
                      >
                        {child.title}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ) : null
            )}
          </List>
        </Collapse>
      ) : (
        <Popper
          open={popperOpen}
          anchorEl={anchorRef.current}
          placement="right-start"
          transition
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
          modifiers={[
            { name: 'offset', options: { offset: [8, 8] } },
            { name: 'preventOverflow', options: { boundary: 'viewport' } }
          ]}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
              <Paper
                elevation={0}
                sx={{
                  minWidth: 210,
                  maxWidth: 300,
                  bgcolor: '#fff',
                  border: '1px solid var(--appMain100)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 28px rgba(36,58,110,0.13), 0 2px 8px rgba(36,58,110,0.07)'
                }}
              >
                <ClickAwayListener onClickAway={handleClickAway}>
                  <Box>
                    <Box
                      sx={{
                        px: 2,
                        py: 1.25,
                        borderBottom: '1px solid var(--appMain100)',
                        bgcolor: 'var(--appMain50)'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: 'var(--appMain500)',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          lineHeight: 1
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>

                    <MenuList dense sx={{ maxHeight: 420, overflow: 'auto', py: 0.75, px: 0.75 }}>
                      {item.children?.map((child) =>
                        child.type === 'item' ? (
                          <MenuItem
                            key={child.id}
                            component={child.url ? Link : 'div'}
                            to={child.url || undefined}
                            onClick={() => setPopperOpen(false)}
                            selected={isChildSelected(child)}
                            sx={{
                              py: 0.85,
                              px: 1.5,
                              borderRadius: '8px',
                              mb: 0.25,
                              '&.Mui-selected': {
                                bgcolor: '#e4edf8',
                                '& .MuiTypography-root': { color: 'var(--appMainMain)', fontWeight: 600 }
                              },
                              '&:hover': {
                                bgcolor: 'var(--appMain50)',
                                '& .MuiTypography-root': { color: 'var(--appMain800)' }
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 22 }}>
                              <FiberManualRecordIcon
                                sx={{
                                  fontSize: isChildSelected(child) ? 7 : 5.5,
                                  color: isChildSelected(child) ? 'var(--appMainMain)' : 'var(--appMain300)'
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    color: isChildSelected(child) ? 'var(--appMainMain)' : 'var(--appMain700)',
                                    fontWeight: isChildSelected(child) ? 600 : 400,
                                    lineHeight: 1.2
                                  }}
                                >
                                  {child.title}
                                </Typography>
                              }
                            />
                          </MenuItem>
                        ) : (
                          <Divider key={child.id} sx={{ my: 0.5, borderColor: 'var(--appMain100)' }} />
                        )
                      )}
                    </MenuList>
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
}

NavCollapse.propTypes = {
  item: PropTypes.object.isRequired,
  level: PropTypes.number
};
